import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Elements as StripeElements } from '@stripe/react-stripe-js';
import {
  RecurlyProvider,
  Elements as RecurlyElements,
} from '@recurly/react-recurly';

import { PaymentSystem } from '../../constants';
import { getPaymentSystem, getPublicKeys } from '../../utils/storage';

const stripeInstances = {};

const Provider = ({ children }) => {
  const paymentSystem = getPaymentSystem();
  const publicKeys = getPublicKeys();
  const stripePublicKey = publicKeys?.stripe?.public_key;
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    if (paymentSystem !== PaymentSystem.STRIPE) {
      setStripe(null);
      return undefined;
    }

    if (!stripePublicKey) {
      return undefined;
    }

    let isMounted = true;

    const initStripe = () => {
      if (window.Stripe && isMounted) {
        stripeInstances[stripePublicKey] =
          stripeInstances[stripePublicKey] || window.Stripe(stripePublicKey);
        setStripe((currentStripe) => (
          currentStripe === stripeInstances[stripePublicKey]
            ? currentStripe
            : stripeInstances[stripePublicKey]
        ));
        return true;
      }

      return false;
    };

    if (initStripe()) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (initStripe()) {
        window.clearInterval(intervalId);
      }
    }, 50);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [paymentSystem, stripePublicKey]);

  if (paymentSystem === PaymentSystem.RECURLY) {
    return (
      <RecurlyProvider publicKey={publicKeys.recurly?.public_key}>
        <RecurlyElements>{children}</RecurlyElements>
      </RecurlyProvider>
    );
  }
  if (paymentSystem === PaymentSystem.STRIPE) {
    if (!stripe) {
      return null;
    }

    // Card-field placeholders ("Card number", "MM / YY", "CVC") come from Stripe
    // and follow the Elements locale, not the page. <html lang> is set by the
    // app's localization init, so the fields render in the funnel's language.
    const locale = (document.documentElement.lang || 'auto').split('-')[0];

    return <StripeElements stripe={stripe} options={{ locale }}>{children}</StripeElements>;
  }
  if (paymentSystem === PaymentSystem.SOLIDGATE) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  if (paymentSystem === PaymentSystem.PADDLE) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  if (paymentSystem === PaymentSystem.TRUEGATE) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  return null;
};

Provider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Provider;
